import React, { useContext, forwardRef } from 'react';
import { View, Text, ViewPropTypes, TouchableOpacity, Platform, StyleSheet, Dimensions } from 'react-native';

import { TextPropTypes } from 'deprecated-react-native-prop-types';
import { has as _has, isUndefined as _isUndefined, isFunction as _isFunction } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import ContentManager from '../../ContentManager';
import { colorPropType } from '../../Utils/PropTypesValidators';
import { Icon, IconTypes, IconTypesValues } from '../Icon';
import { Colors, AllowFontScalingContext } from '../Themes';
import VFText from '../VFText';

import { OutlinedTextField } from './MaterialTextField';
import styles from './VFInput.Styles';

function getHelperTextStyle(color) {
  const stylesArr = [styles.helperTextStyle];
  stylesArr.push({ color: color });
  return StyleSheet.flatten(stylesArr);
}

const getHelperTextStyleMemoized = memoize(getHelperTextStyle);

function setUpRef(inputRef, forwardedRef, fieldRefProp) {
  if (_isFunction(forwardedRef)) {
    forwardedRef(inputRef);
  } else if (!_isUndefined(forwardedRef)) {
    if (_has(forwardedRef, 'current')) {
      forwardedRef.current = inputRef;
    } else {
      forwardedRef = inputRef;
    }
  }

  if (_isFunction(fieldRefProp)) {
    fieldRefProp(inputRef);
  } else if (!_isUndefined(fieldRefProp)) {
    if (_has(fieldRefProp, 'current')) {
      fieldRefProp.current = inputRef;
    } else {
      fieldRefProp = inputRef;
    }
  }
}

const VFInput = forwardRef(
  (
    {
      leftIconName,
      leftIconType,
      leftIconColor,
      leftIconSize,
      leftIconStyle,
      showLeftIcon,
      rightIconName,
      rightIconType,
      rightIconColor,
      rightIconSize,
      rightIconStyle,
      rightIconPress,
      rigthIconTestID,
      showRightIcon,
      value,
      onChangeText,
      showHelperText,
      helperText,
      customHelperText,
      helperTextColor,
      helperTextTestID,
      selectionColor,
      tintColor,
      baseColor,
      labelFontSize,
      fontSize,
      upperLabel,
      label,
      textColor,
      labelTextStyle,
      containerStyle,
      containerAccessibilityLabel,
      leftIconAccessibilityLabel,
      rightIconAccessibilityLabel,
      inputAccessibilityLabel,
      helperAccessibilityLabel,
      allowFontScaling,
      fieldRef,
      containerTestID,
      testID,
      textInputContainerStyle,
      contentInset,
      prefix,
      suffix,
      disableRightIconPress,
      rightIconContainerStyle,
      inputProps,
      ...props
    },
    ref,
  ) => {
    const helperTextStyle = getHelperTextStyleMemoized(helperTextColor);
    const _allowFontScaling = useContext(AllowFontScalingContext);
    const accessibilityProps = {
      testID: testID ? testID : inputAccessibilityLabel,
    };
    const _contentInset = {
      input: 12.5,
      label: 0,
    };

    if (showRightIcon) {
      _contentInset.right = rightIconSize + 17;
    }

    if (Platform.OS === 'web') {
      if (Dimensions.get('window').width > 700) {
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext("2d");
        if (labelTextStyle?.fontFamily) {
          ctx.font = `${labelTextStyle?.fontSize || fontSize}px ${labelTextStyle?.fontFamily}`
        } else {
          const text = "16px VodafoneRg"
          ctx.font = text
        }
        ctx.fontSize = fontSize;
        var width = ctx.measureText(ContentManager.translate(label)).width;
        _contentInset.labelWidthWeb = width
      } else {
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext("2d");
        ctx.fontSize = fontSize;
        var width = ctx.measureText(label).width;
        _contentInset.labelWidthWeb = width
      }
    }

    return (
      <View
        style={[styles.containerStyle, containerStyle]}
        accessibilityLabel={containerAccessibilityLabel}
        testID={containerTestID}
      >
        <View style={styles.inputContainerStyle}>
          {showLeftIcon ? (
            <View style={styles.leftIconContainerStyle}>
              <Icon
                name={leftIconName}
                type={leftIconType}
                color={leftIconColor}
                size={leftIconSize}
                style={leftIconStyle}
                accessibilityLabel={leftIconAccessibilityLabel}
              />
            </View>
          ) : null}

          <OutlinedTextField
            {...props}
            {...accessibilityProps}
            ref={inputRef => setUpRef(inputRef, ref, fieldRef)}
            contentInset={{ ...contentInset, ..._contentInset }}
            selectionColor={selectionColor}
            upperLabel={upperLabel && ContentManager.translate(upperLabel)}
            label={label ? ContentManager.translate(label) : undefined}
            tintColor={tintColor}
            baseColor={baseColor}
            activeLineWidth={1}
            disabledLineWidth={1}
            lineWidth={1}
            labelFontSize={labelFontSize}
            fontSize={fontSize}
            textColor={textColor}
            labelTextStyle={[styles.labelTextStyle, labelTextStyle]}
            suffix={suffix}
            prefix={prefix}
            value={value}
            onChangeText={onChangeText}
            containerStyle={styles.textInputStyle}
            inputContainerStyle={textInputContainerStyle}
            allowFontScaling={
              _isUndefined(_allowFontScaling) ? allowFontScaling : _allowFontScaling
            }
            inputProps={inputProps}
          />
          {showRightIcon ? (
            <TouchableOpacity
              style={[styles.rightIconContainerStyle, rightIconContainerStyle]}
              onPress={rightIconPress}
              accessibilityLabel={rightIconAccessibilityLabel}
              disabled={disableRightIconPress}
              testID={rigthIconTestID}
              accessible={false}
            >
              <Icon
                name={rightIconName}
                type={rightIconType}
                color={rightIconColor}
                size={rightIconSize}
                style={rightIconStyle}
              />
            </TouchableOpacity>
          ) : null}
        </View>

        {showHelperText ? (
          <View style={styles.helperTextContainerStyle}>
            {customHelperText ? (
              customHelperText
            ) : (
              <VFText
                i18nKey={helperText}
                style={helperTextStyle}
                accessibilityLabel={helperAccessibilityLabel}
                accessibilityRole="text"
                testID={helperTextTestID}
              />
            )}
          </View>
        ) : null}
      </View>
    );
  }
);

VFInput.defaultProps = {
  leftIconType: IconTypes.image,
  leftIconSize: 24,
  showLeftIcon: false,
  rightIconType: IconTypes.image,
  rightIconSize: 24,
  showRightIcon: false,
  showHelperText: false,
  rightIconPress: () => { },
  labelFontSize: 14.6,
  fontSize: 18.7,
  isValid: true,
  selectionColor: Platform.OS === 'ios' ? Colors.vfRed : Colors.vfRedRGBA(0.2),
  tintColor: Colors.dark_grey,
  baseColor: Colors.dark_grey,
  textColor: Colors.dark_grey,
  helperTextColor: Colors.vfRed,
  labelTextStyle: { color: Colors.light_grey },
  onChangeText: () => { },
  isSearchable: false,
  isSearchItemExist: false,
  contentInset: {},
  prefix: null,
  suffix: null,
  disableRightIconPress: false,
  upperLabel: undefined,
};

VFInput.propTypes = {
  fieldRef: PropTypes.any,
  leftIconName: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ uri: PropTypes.string })]),
  leftIconType: PropTypes.oneOf(IconTypesValues),
  leftIconColor: colorPropType,
  leftIconSize: PropTypes.number,
  leftIconStyle: ViewPropTypes.style,
  showLeftIcon: PropTypes.bool,
  rightIconName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ uri: PropTypes.string }),
  ]),
  rightIconType: PropTypes.oneOf(IconTypesValues),
  rightIconColor: colorPropType,
  rightIconSize: PropTypes.number,
  rightIconStyle: ViewPropTypes.style,
  rightIconContainerStyle: ViewPropTypes.style,
  rightIconPress: PropTypes.func,
  disableRightIconPress: PropTypes.bool,
  showRightIcon: PropTypes.bool,
  showHelperText: PropTypes.bool,
  helperText: PropTypes.string,
  customHelperText: PropTypes.any,
  helperTextColor: colorPropType,
  helperTextTestID: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onChangeText: PropTypes.func,
  isValid: PropTypes.bool,
  labelFontSize: PropTypes.number,
  fontSize: PropTypes.number,
  selectionColor: colorPropType,
  tintColor: colorPropType,
  baseColor: colorPropType,
  textColor: colorPropType,
  labelTextStyle: TextPropTypes.style,
  containerStyle: ViewPropTypes.style,
  inputContainerStyle: ViewPropTypes.style,
  containerAccessibilityLabel: PropTypes.string,
  leftIconAccessibilityLabel: PropTypes.string,
  rightIconAccessibilityLabel: PropTypes.string,
  inputAccessibilityLabel: PropTypes.string,
  helperAccessibilityLabel: PropTypes.string,
  allowFontScaling: PropTypes.bool,
  rigthIconTestID: PropTypes.string,
  testID: PropTypes.string,
  containerTestID: PropTypes.string,
  isSearchable: PropTypes.bool,
  isSearchItemExist: PropTypes.bool,
  textInputContainerStyle: OutlinedTextField.propTypes.inputContainerStyle,
  contentInset: OutlinedTextField.propTypes.contentInset,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  upperLabel: PropTypes.string,
};

export default VFInput;
